/**
 * CORE-875 — правила шага 2 «Содержание», отдельно от рендера.
 *
 * Здесь перестановки и подписи: то, что легко сломать молча и почти невозможно
 * проверить глазами, перетаскивая карточки в браузере.
 */
import type {
  ProgramContent,
  ProgramItem,
  ProgramItemKind,
  ProgramSection,
} from '../../../types/programs';

/** Вкладки правой колонки — порядок как в макете. */
export const PICKER_TABS: { kind: ProgramItemKind; label: string; icon: string }[] = [
  { kind: 'practice', label: 'Практики', icon: 'leaf-outline' },
  { kind: 'media', label: 'Медиа', icon: 'film-outline' },
  { kind: 'live', label: 'Live', icon: 'radio-outline' },
  { kind: 'file', label: 'Файлы', icon: 'document-attach-outline' },
];

/**
 * Иконка типа материала. ТЗ требует «самостоятельные иконки», а не общий
 * росток: в составе программы четыре разных типа, и различать их взглядом
 * важнее, чем держать единый значок раздела.
 */
export function kindIcon(kind: ProgramItemKind): string {
  switch (kind) {
    case 'live': return 'radio-outline';
    case 'file': return 'document-attach-outline';
    case 'media': return 'film-outline';
    default: return 'leaf-outline';
  }
}

export function kindLabel(kind: ProgramItemKind): string {
  switch (kind) {
    case 'live': return 'Live';
    case 'file': return 'Файл';
    case 'media': return 'Медиа';
    default: return 'Практика';
  }
}

/** «12 МБ», «340 КБ». Ноль — это ноль, а не «неизвестно». */
export function formatFileSize(bytes: number | null): string {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} КБ`;
  const mb = bytes / (1024 * 1024);
  return `${mb >= 10 ? Math.round(mb) : Math.round(mb * 10) / 10} МБ`;
}

/** «45 мин» / «1 ч 20 мин». Пустая длительность не печатается вовсе. */
export function formatDuration(minutes: number | null): string {
  if (minutes == null || minutes <= 0) return '';
  if (minutes < 60) return `${minutes} мин`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${hours} ч` : `${hours} ч ${rest} мин`;
}

/** «20 августа, 19:00» для эфира. Битую дату не печатаем. */
export function formatStartsAt(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('ru-RU', {
    day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit',
  });
}

/** Вторая строка карточки материала: тип плюс то, что о нём вообще известно. */
export function itemSubtitle(item: ProgramItem): string {
  const parts = [kindLabel(item.kind)];
  if (item.kind === 'live') {
    const at = formatStartsAt(item.startsAtUtc);
    if (at) parts.push(at);
  } else if (item.kind === 'file') {
    const size = formatFileSize(item.fileSizeBytes);
    if (size) parts.push(size);
  } else {
    const dur = formatDuration(item.durationMinutes);
    if (dur) parts.push(dur);
  }
  return parts.join(' · ');
}

/**
 * Переставить элемент внутри списка. Возвращает НОВЫЙ список id — именно его
 * ждёт сервер: порядок присылается целиком, а не «сдвинь X на N».
 *
 * Индексы вне списка возвращают его же: перетаскивание за границу и нажатие
 * «вверх» на первом элементе — это «ничего не произошло», а не ошибка.
 */
export function moveInList<T>(list: T[], from: number, to: number): T[] {
  if (from === to) return list;
  if (from < 0 || from >= list.length) return list;
  if (to < 0 || to >= list.length) return list;
  const next = [...list];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
}

/** Ids раздела в текущем порядке — то, что уходит в reorder после перестановки. */
export function sectionItemIds(section: ProgramSection): number[] {
  return section.items.map((i) => i.id);
}

/**
 * Список id для целевого раздела после переноса материала из соседнего.
 *
 * Сервер принимает id из другого раздела той же программы и переселяет их —
 * поэтому «перенести» и «переставить» это один и тот же вызов, и фронту не
 * нужно решать, что именно сделал мастер.
 */
export function idsAfterMoveBetweenSections(
  target: ProgramSection, itemId: number, toIndex: number,
): number[] {
  const ids = sectionItemIds(target).filter((id) => id !== itemId);
  const index = Math.max(0, Math.min(toIndex, ids.length));
  ids.splice(index, 0, itemId);
  return ids;
}

/** Раздел, в котором сейчас лежит материал. */
export function findSectionOfItem(content: ProgramContent, itemId: number): ProgramSection | null {
  return content.sections.find((s) => s.items.some((i) => i.id === itemId)) ?? null;
}

/**
 * Куда добавлять выбранное, когда мастер не выбрал раздел явно.
 *
 * Первый раздел, а не последний: пустая программа получает один раздел, и
 * «добавить» должно попадать именно в него. Возвращает null, если разделов
 * ещё нет — тогда интерфейс сначала просит создать раздел, а не молча теряет
 * выбор мастера.
 */
export function defaultTargetSectionId(content: ProgramContent): number | null {
  return content.sections[0]?.id ?? null;
}

/** Подпись под кнопкой добавления: сколько выбрано и куда это уедет. */
export function addButtonLabel(selectedCount: number): string {
  if (selectedCount === 0) return 'Добавить выбранное';
  return `Добавить выбранное (${selectedCount})`;
}

/**
 * Итог батча словами. «Добавлено 3» и «2 уже были в разделе» — разные вещи, и
 * молчать про второе значит оставить мастера гадать, почему выбрал пять, а
 * приехало три.
 */
export function addResultMessage(added: number, skipped: number): string | null {
  if (added === 0 && skipped === 0) return null;
  if (skipped === 0) return `Добавлено: ${added}.`;
  if (added === 0) return `Уже в разделе: ${skipped}. Ничего не добавили.`;
  return `Добавлено: ${added}. Уже были в разделе: ${skipped}.`;
}

/** Ключ выбора в правой колонке: у файлов нет id, они опознаются по url. */
export function candidateKey(c: { kind: ProgramItemKind; referenceId: number | null; fileUrl: string | null }): string {
  return c.kind === 'file' ? `file:${c.fileUrl}` : `${c.kind}:${c.referenceId}`;
}
